import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const savedUser = localStorage.getItem('user');
      if (savedUser) {
        setUser(JSON.parse(savedUser));
      }
    }
    setLoading(false);
  }, [token]);

  // Persist a freshly issued session (shared by login and verifyEmail).
  const applySession = (newToken, userData) => {
    setToken(newToken);
    setUser(userData);
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { token: newToken, user: userData } = response.data;
      applySession(newToken, userData);
      return { success: true };
    } catch (error) {
      // 403 = account exists but email not verified → prompt for the code.
      if (error.response?.status === 403) {
        return { success: false, needsVerification: true, email, error: error.response?.data?.detail || 'Email not verified' };
      }
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  // Register no longer logs the user in — it triggers an activation email.
  const register = async (userData) => {
    try {
      await axios.post(`${API}/auth/register`, userData);
      return { success: true, needsVerification: true, email: userData.email };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Registration failed' };
    }
  };

  // Confirm the emailed activation code — logs the user in on success.
  const verifyEmail = async (email, code) => {
    try {
      const response = await axios.post(`${API}/auth/verify-email`, { email, code });
      const { token: newToken, user: userData } = response.data;
      applySession(newToken, userData);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Verification failed' };
    }
  };

  const resendVerification = async (email) => {
    try {
      await axios.post(`${API}/auth/resend-verification`, { email });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Could not resend code' };
    }
  };

  const forgotPassword = async (email) => {
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Request failed' };
    }
  };

  const resetPassword = async (email, code, newPassword) => {
    try {
      await axios.post(`${API}/auth/reset-password`, { email, code, new_password: newPassword });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Reset failed' };
    }
  };

  // Refresh the logged-in user from the server (e.g. after verification changes)
  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
      return response.data;
    } catch {
      return null;
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, verifyEmail, resendVerification, forgotPassword, resetPassword, logout, refreshUser, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};